import React from 'react';
import { Cpu, HeartHandshake, Lock, Sparkles } from 'lucide-react';
import Alt6Layout from '../components/Alt6Layout';
import {
  CtaBand,
  Eyebrow,
  Reveal,
  SectionHead,
} from '../components/Alt6Shared';

const PRINCIPLES = [
  {
    icon: Cpu,
    title: 'AI-first, not AI-added',
    body: 'We didn\'t bolt a chatbot onto a twenty-year-old PMS, and we didn\'t bolt a PMS onto a chatbot. The system was designed from the first line of code for AI to do real work in it.',
  },
  {
    icon: HeartHandshake,
    title: 'Your team stays in charge',
    body: 'AI should absorb the drudgery, not the judgment. Your policies decide what runs autonomously, and your people handle the moments that need a human.',
  },
  {
    icon: Lock,
    title: 'Trust is the product',
    body: 'HIPAA-compliant by design, every action logged and auditable. An operating system for a medical practice has to earn its autonomy — ours does, visibly.',
  },
  {
    icon: Sparkles,
    title: 'The whole job, not a demo',
    body: 'Answering a call is easy. Answering it, verifying the benefits, filing the claim, posting the EOB, and bringing the patient back for recall — that\'s the job. We build for the whole job.',
  },
];

export default function Alt6WhyAsha() {
  return (
    <Alt6Layout title="Why Asha">
      <section className="a6-page-hero">
        <div className="a6-container a6-page-hero-inner">
          <Reveal>
            <Eyebrow>Why Asha</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h1>
              Built for the day AI <em>runs the office</em>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p>
              Every practice management system will claim AI features this year. We
              started from a different question: if AI could genuinely run the
              administrative side of a dental practice, what would the system
              underneath it have to look like? Then we built that system.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="a6-section">
        <div className="a6-container">
          <SectionHead
            eyebrow="Our principles"
            title="What we believe about AI in dentistry"
          />
          <div className="a6-grid a6-grid-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 0.08}>
                <div className="a6-card">
                  <span className="a6-card-icon">
                    <p.icon size={20} strokeWidth={2.2} />
                  </span>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="a6-section a6-wash">
        <div className="a6-container">
          <Reveal className="a6-band">
            <Eyebrow>The team</Eyebrow>
            <h2 className="a6-h2">Serious people, serious backing.</h2>
            <p className="a6-lede">
              Asha is built by AI product leaders and clinicians who have shipped
              healthcare systems used at national scale, and backed by some of the
              world's leading technology investors. We're in this for the long
              arc — the practices that run on us deserve nothing less.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Meet the team behind the OS."
        body="Book a demo and you'll talk to the people who build the product — not a script."
      />
    </Alt6Layout>
  );
}
